import BaseComponent from '@js/BaseComponent'
import Animator from '@js/Animator'
import { easeOutQuad as easing } from '@js/easing'

import {
    hasMotion,
    onHasMotion,
    hasMouse
} from '@js/mediaQueries'

/**
 * An individual headline to apply the magnifying glass effect to.
 * @extends BaseComponent
 */
class HeadlineEffect extends BaseComponent {
    /**
     * Ligature pairs supported by the headline font.
     *
     * Adding pairs the font supports to this array ensures they're grouped in
     * the same `span` tag and retain their ligature appearance.
     *
     * @type {Array}
     */
    static LIGATURE_PAIRS = ['rs', 'Te']

    /**
     * An array containing each of the headline's span elements
     * @type {Array}
     */
    spans = []

    /**
     * Stores the bounding rect information for each span
     * @type {Array}
     */
    charCenters = []

    constructor(element) {
        super(element)

        if (sir.isEditor) return

        if (getComputedStyle(this.element).getPropertyValue('font-variation-settings') === 'normal') {
            return
        }

        if (
            this.element.closest('.disable-heading-effect') ||
            this.element.closest('.disable-heading-style')
        ) {
            return
        }

        this.minWeight = parseInt(getComputedStyle(element).getPropertyValue('--font-wght-min'))
        this.maxWeight = parseInt(getComputedStyle(element).getPropertyValue('--font-wght-max'))
        this.buffer = parseInt(getComputedStyle(element).getPropertyValue('--font-wght-buffer'))

        let regex
        if (this.element.textContent.length < 90) {
            const ligatures = HeadlineEffect.LIGATURE_PAIRS.slice()
            ligatures.push('\\p{Emoji}')
            ligatures.push('.')

            regex = new RegExp(ligatures.join('|'), 'gu')
        } else {
            regex = new RegExp('\\w+', 'g')
        }

        this.element.ariaLabel = this.element.textContent.replace(/(?:\[\p{Emoji}+\]) /gu, '')
        this.element.innerHTML = this.element.textContent.replace(regex, '<span aria-hidden="true">$&</span>')
        this.spans = [...this.element.querySelectorAll('span')]
        this.spans.forEach((span, i, spans) => {
            const empty = spans.slice(0, i).filter(elem => elem.innerHTML === ' ').length
            const mult = i - (empty * 4)
            span.style.setProperty('--delay', `${mult * 75}ms`)

            if (span.textContent.match(/\p{Emoji}+/gu)) {
                span.classList.add('emoji')
            } else if (span.textContent.match(/…/gu)) {
                // The ellipsis character in the headline font is messed up and
                // looks awful. The two options to fix it were A) Set a fixed
                // weight and B) replace with three periods. The former is
                // semantically correct but doesn't animate and looks strange.
                // The latter looks "correct" but isn't an ellipsis :/

                // span.classList.add('ellipsis')
                span.innerHTML = '...'
            }
        })
        this.updateSpanCenters()

        if (hasMotion()) {
            this.element.classList.add('text-gradient')
            this.element.classList.add('weight-transition')
        }

        onHasMotion(hasMotion => {
            if (hasMotion) {
                this.element.classList.add('text-gradient')
                this.element.classList.add('weight-transition')
            } else {
                this.element.classList.remove('text-gradient')
                this.element.classList.remove('weight-transition')
            }
        })

        if (hasMotion()) {
            this.addEvents()
        }

        onHasMotion(hasMotion => {
            if (hasMotion) {
                this.addEvents()
            } else {
                this.removeEvents()
                this.reset()
            }
        })
    }

    addEvents = () => {
        window.addEventListener('resize', this.updateSpanCenterss)
        if (hasMouse()) {
            this.element.addEventListener('mouseenter', this.onMouseEnter)
            this.element.addEventListener('mouseleave', this.onMouseLeave)
        } else {
            this.element.addEventListener('touchstart', this.triggerTouchAnimation)
        }
    }

    removeEvents = () => {
        window.removeEventListener('resize', this.updateSpanCenterss)
        if (hasMouse()) {
            this.element.removeEventListener('mouseenter', this.onMouseEnter)
            this.element.removeEventListener('mouseleave', this.onMouseLeave)
            this.element.removeEventListener('mousemove', this.trackMouse)
        } else {
            this.element.removeEventListener('touchstart', this.triggerTouchAnimation)
        }
    }

    onMouseEnter = () => {
        this.element.addEventListener('mousemove', this.trackMouse)
    }

    onMouseLeave = () => {
        this.element.removeEventListener('mousemove', this.trackMouse)
        this.reset()
    }

    /**
     * Updates the weight of each character using the given mouse coords
     * @param {MouseEvent} event
     */
    trackMouse = (event) => {
        const {
            left: parentLeft,
            top: parentTop
        } = this.element.getBoundingClientRect()

        const mouseX = event.pageX - parentLeft - window.scrollX
        const mouseY = event.pageY - parentTop - window.scrollY

        this.spans.forEach((span, spanIndex) => {
            const { centerX, centerY } = this.charCenters[spanIndex]

            // X distance is calculated 1:1.
            // Y distance is calculated with a multiplier to make it more sensitive in that axis.
            const dist = Math.hypot(
                centerX - mouseX,
                (centerY - mouseY) * 3
            )

            if (dist > this.buffer) {
                span.style.removeProperty('font-variation-settings')
            } else {
                const overflow = 20
                const normalizedDist = 1 - (dist / this.buffer)
                const range = (this.maxWeight + overflow) - this.minWeight
                const weight = Math.min(normalizedDist * range + this.minWeight, this.maxWeight)
                span.style.setProperty(
                    'font-variation-settings',
                    `"wght" ${weight}`
                )
            }
        })

        this.element.style.setProperty(
            '--gradient-position-x',
            `${mouseX}px`
        )

        this.element.style.setProperty(
            '--gradient-position-y',
            `${mouseY}px`
        )
    }

    /**
     * Triggers the touch-based version of the headline effect starting from the
     * span that was touched.
     *
     * @param {TouchEvent} event
     */
    triggerTouchAnimation = (event) => {
        const target = event.target

        if (target.tagName !== 'SPAN') {
            return
        }

        this.element.classList.remove('trigger-animation')
        const spanIndex = this.spans.indexOf(target)
        const { centerX, centerY } = this.charCenters[spanIndex]
        this.spans.forEach((span, i) => {
            const {
                centerX: thisCenterX,
                centerY: thisCenterY
            } = this.charCenters[i]
            const distance = Math.hypot(
                centerX - thisCenterX,
                (centerY - thisCenterY) * 2
            )
            span.style.animation = 'none'
            span.style.setProperty('--delay', `${distance * 1.75}ms`)
            void span.offsetWidth
            span.style.animation = null
        })
        this.element.classList.add('trigger-animation')
    }

    /**
     * Resets each character fully so the effect doesn't get stuck
     */
    reset() {
        this.spans.forEach(span => {
            const property = getComputedStyle(span).getPropertyValue('font-variation-settings')
            const regex = /"wght" (\d{1,3})/
            const result = property.match(regex)
            if (result.length > 1) {
                const animator = new Animator(
                    parseInt(result[1]),
                    this.minWeight,
                    (value) => {
                        span.style.setProperty(
                            'font-variation-settings',
                            `"wght" ${value}`
                        )
                    }
                )

                animator.duration = 120
                animator.easing = easing
                animator.onEnd = () => span.style.removeProperty('font-variation-settings')
                animator.start()
            } else {
                span.style.removeProperty('font-variation-settings')
            }
        })
    }

    /**
     * Updates the bounding rect for the headline and each inner span
     */
    updateSpanCenters() {
        const parentRect = this.element.getBoundingClientRect()
        this.charCenters = this.spans.map(span => {
            const spanRect = span.getBoundingClientRect()
            const centerX = (spanRect.left - parentRect.left) + spanRect.width / 2
            const centerY = (spanRect.top - parentRect.top) + spanRect.height / 2
            return {
                centerX, centerY
            }
        })
    }
}

HeadlineEffect.create('h1, h2, h3, h4, h5, h6')
